/**
 * Trigger SSI Reset for Lead Clinician
 * 
 * This utility calls the resetSSIForLeadClinician cloud function when a Lead clinician
 * needs to reset the SSI section due to a conflict with their CI/DD decisions.
 * 
 * Used in the 3 Borough / SLaM workflow where:
 * 1. SSI is done first by a junior clinician
 * 2. Lead clinician does CI + DD + PAD
 * 3. Lead's findings may conflict with SSI recommendations
 * 4. Lead can reset SSI to modify symptom/domain decisions and regenerate paragraphs
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Trigger SSI reset for the Lead clinician
 * 
 * This function:
 * 1. Calls the resetSSIForLeadClinician cloud function
 * 2. Archives the current SSI data before clearing
 * 3. Clears the SSI report paragraphs (outputs, approvedParagraphs)
 * 4. Sets ssiResetRequired = true to grant Lead access to SSI module
 * 5. Returns the result including the archive ID
 * 
 * @param {string} patientId - The patient ID
 * @param {Object} conflictDetails - Details about the conflict from detectSSIConflict()
 * @param {string} conflictDetails.conflictType - 'CI' or 'DD'
 * @param {string} conflictDetails.ssiRecommendation - SSI clinician's recommendation
 * @param {string} conflictDetails.currentDecision - Lead clinician's decision
 * @param {boolean} conflictDetails.isCommonCase - Whether this is the common case (SSI negative, Lead positive)
 * @param {string} [reason] - Optional custom reason for the reset
 * 
 * @returns {Promise<Object>} - Result object with success, message, and archiveId
 */
export const TriggerResetSSI = async (patientId, conflictDetails, reason = null) => {
    console.log('🔄 TriggerResetSSI: Starting SSI reset for patient', patientId);
    console.log('Conflict details:', conflictDetails);

    try {
        // Get the callable function reference (uses pre-configured functions instance from firebase.js)
        const resetSSIForLeadClinician = httpsCallable(functions, 'resetSSIForLeadClinician');

        // Prepare the request data
        const requestData = {
            patientId,
            conflictType: conflictDetails?.conflictType || 'Unknown',
            conflictDetails: {
                ssiRecommendation: conflictDetails?.ssiRecommendation,
                currentDecision: conflictDetails?.currentDecision,
                isCommonCase: conflictDetails?.isCommonCase || false,
            },
            reason: reason || `Conflict with Lead clinician findings at ${conflictDetails?.conflictType || 'assessment'}`,
        };

        console.log('Calling resetSSIForLeadClinician with data:', requestData);

        // Call the cloud function
        const result = await resetSSIForLeadClinician(requestData);

        console.log('✅ SSI Reset successful:', result.data);

        return {
            success: true,
            message: result.data.message,
            archiveId: result.data.archiveId,
        };

    } catch (error) {
        console.error('❌ Error resetting SSI:', error);

        // Extract error message from Firebase Functions error
        let errorMessage = 'An error occurred while resetting SSI.';
        
        if (error.code === 'functions/permission-denied') {
            errorMessage = 'Only the Lead clinician can reset the SSI section.';
        } else if (error.code === 'functions/failed-precondition') {
            errorMessage = 'SSI report is not approved yet. Reset is only needed when there is an approved SSI report.';
        } else if (error.code === 'functions/not-found') {
            errorMessage = 'Patient not found.';
        } else if (error.code === 'functions/unauthenticated') {
            errorMessage = 'You must be logged in to reset SSI.';
        } else if (error.message) {
            errorMessage = error.message;
        }

        return {
            success: false,
            message: errorMessage,
            error: error,
        };
    }
};

export default TriggerResetSSI;
